import { useEffect, useLayoutEffect, useRef } from 'react';
import Konva from 'konva';

interface OutlineProps {
  x: number;
  y: number;
  width: number;
  height: number;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
  fill: string;
  listening: false;
}

interface HighlightProps {
  x: number;
  y: number;
  width: number;
  height: number;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
  fill: string;
  listening: false;
}

interface UseOrgNodeEntranceResult {
  contentGroupRef: React.RefObject<Konva.Group | null>;
  highlightRef: React.RefObject<Konva.Rect | null>;
  outlineProps: OutlineProps | null;
  highlightProps: HighlightProps | null;
}

/** Same blue used by the search highlight */
const HIGHLIGHT_COLOR = '#2563eb';
const HIGHLIGHT_STROKE_WIDTH = 3;

/**
 * Entrance & collapse highlight animation for org chart nodes.
 *
 * **Entrance (new nodes appearing on expand):**
 * 1. Ghost outline while card content fades in over 300 ms.
 * 2. Blue highlight outline appears, strokeWidth tweens 3 → 0 over 1.5 s.
 *
 * **Collapse highlight (parent node after branch collapses):**
 * Same blue highlight animation (strokeWidth 3 → 0) triggered on demand via
 * `collapseHighlight`.
 *
 * strokeWidth is managed imperatively (not via React props) so re-renders
 * cannot reset it mid-tween.
 */
export function useOrgNodeEntrance(
  enabled: boolean,
  isExiting: boolean,
  cardWidth: number,
  cardHeight: number,
  cornerRadius: number,
  collapseHighlight = false,
): UseOrgNodeEntranceResult {
  const contentGroupRef = useRef<Konva.Group | null>(null);
  const highlightRef = useRef<Konva.Rect | null>(null);
  const didAnimateRef = useRef(false);
  const tweenRef = useRef<Konva.Tween | null>(null);
  const hlTweenRef = useRef<Konva.Tween | null>(null);
  const hlInitRef = useRef(false);

  // Keep highlight rect's strokeWidth at 0 before any animation triggers it.
  // Nodes can be hidden (return null) and shown again by filter changes, so we
  // must re-initialize when the rect instance is recreated.
  useLayoutEffect(() => {
    const hlEl = highlightRef.current;
    if (!hlEl) {
      hlInitRef.current = false;
      return;
    }
    if (!hlInitRef.current) {
      hlEl.strokeWidth(0);
      hlInitRef.current = true;
    }
  });

  // --- Entrance animation (new nodes) ---
  useEffect(() => {
    if (!enabled || didAnimateRef.current || isExiting) return;
    const contentEl = contentGroupRef.current;
    if (!contentEl) return;

    didAnimateRef.current = true;
    contentEl.opacity(0);

    const tween = new Konva.Tween({
      node: contentEl,
      duration: 0.5,
      opacity: 1,
      easing: Konva.Easings.EaseOut,
      onFinish: () => {
        tweenRef.current = null;
        playHighlight();
        contentEl.getLayer()?.batchDraw();
      },
    });
    tweenRef.current = tween;
    tween.play();

    return () => {
      tween.destroy();
      tweenRef.current = null;
      didAnimateRef.current = false;
      hlInitRef.current = false;
      if (hlTweenRef.current) {
        hlTweenRef.current.destroy();
        hlTweenRef.current = null;
      }
    };
  }, [enabled, isExiting]);

  // --- Collapse highlight (parent node stays, children exit) ---
  useEffect(() => {
    if (!enabled || !collapseHighlight || isExiting) return;
    playHighlight();
  }, [collapseHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Shared: flash the highlight rect and tween strokeWidth → 0 */
  function playHighlight() {
    const hlEl = highlightRef.current;
    if (!hlEl) return;

    // Kill any running highlight tween
    if (hlTweenRef.current) {
      hlTweenRef.current.destroy();
      hlTweenRef.current = null;
    }

    hlEl.strokeWidth(HIGHLIGHT_STROKE_WIDTH);
    const hlTween = new Konva.Tween({
      node: hlEl,
      duration: 1.5,
      strokeWidth: 0,
      easing: Konva.Easings.EaseOut,
      onFinish: () => {
        hlTweenRef.current = null;
      },
    });
    hlTweenRef.current = hlTween;
    hlTween.play();
  }

  // Ghost outline — visible only until the content fade-in starts
  const outlineProps: OutlineProps | null =
    enabled && !didAnimateRef.current && !isExiting
      ? {
          x: 0,
          y: 0,
          width: cardWidth,
          height: cardHeight,
          stroke: '#CBD5E1',
          strokeWidth: 1.5,
          cornerRadius,
          fill: 'transparent',
          listening: false as const,
        }
      : null;

  // Highlight overlay — always rendered (when enabled) so the ref is
  // available; strokeWidth is managed imperatively (starts at 0).
  const highlightProps: HighlightProps | null =
    enabled && !isExiting
      ? {
          x: 0,
          y: 0,
          width: cardWidth,
          height: cardHeight,
          stroke: HIGHLIGHT_COLOR,
          strokeWidth: 0,
          cornerRadius,
          fill: 'transparent',
          listening: false as const,
        }
      : null;

  return { contentGroupRef, highlightRef, outlineProps, highlightProps };
}
